import { useMemo } from 'react';
import clsx from 'clsx';

import { css, MenuItem, styled } from '@mui/material';

import { getPrimarySubtextsSearchHighlightTarget } from '../../utils';
import { MenuItemContent } from '../MenuItemContent';
import TextHighlights from '../Select/TextHighlights';
import { Tooltip } from '../Tooltip';
import { hasVisibleTooltipMessage } from '../Tooltip/utils';
import { getDynamicClasses, menuOptionItemClasses } from './theme';

import type { MenuOptionItemOwnerState, MenuOptionItemProps } from './types';

/**
 * MenuOptionItem
 *
 * DropdownMenu2, TransferList, SelectableList 등에서 공유되는
 * 개별 옵션 아이템 컴포넌트입니다.
 *
 * @remarks
 * - MenuItem + MenuItemContent의 조합을 단일 컴포넌트로 제공
 * - highlightText 지원으로 검색 결과 하이라이트 가능
 * - tooltip props로 툴팁 기능 지원
 */
export function MenuOptionItem(props: MenuOptionItemProps) {
  const {
    ref,
    className,
    text,
    renderContent,
    subTexts,
    truncateSubTextIndex,
    icon,
    startBadge,
    disableTooltipOnEllipsis = false,
    ellipsisTooltipPlacement = 'right',
    enableHighlight = true,
    endBadge,
    selected = false,
    disabled = false,
    focused = false,
    interactive = true,
    selectedType = 'check',
    highlightText,
    onClick,
    tooltip,
    color = 'default',
    suffix,
    slots,
    slotProps,
    // 드래그 관련 props
    dragHandleRef,
    dragHandleDisabled,
    showDragHandle,
    isDragging,
    onDragStart,
    onDragEnd,
    ...others
  } = props;

  const { root: RootSlotComponent } = slots ?? {};
  const { root: rootSlotProps } = slotProps ?? {};

  const ownerState: MenuOptionItemOwnerState = {
    ...props,
    selected,
    disabled,
    focused,
    interactive,
    selectedType,
    color,
  };

  const highlightTarget = useMemo(() => {
    if (typeof text !== 'string' || !highlightText) return { kind: 'none' as const };
    return getPrimarySubtextsSearchHighlightTarget(text, subTexts, highlightText);
  }, [highlightText, subTexts, text]);

  // 필터·정렬과 동일: 실제 매칭에 쓰인 필드만 하이라이트
  const enableHighlightForText =
    !!highlightText &&
    (typeof enableHighlight === 'object' ? enableHighlight.text : enableHighlight);
  const renderedText =
    enableHighlightForText && typeof text === 'string' && highlightTarget.kind === 'primary' ? (
      <TextHighlights originText={text} highlightText={highlightText} />
    ) : (
      text
    );

  const enableHighlightForSubTexts =
    !!highlightText &&
    (typeof enableHighlight === 'object' ? enableHighlight.subTexts : enableHighlight);
  const renderedSubTexts = subTexts?.map((subText, index) =>
    enableHighlightForSubTexts &&
    typeof subText === 'string' &&
    highlightTarget.kind === 'subText' &&
    highlightTarget.index === index ? (
      <TextHighlights key={subText} originText={subText} highlightText={highlightText} />
    ) : (
      subText
    ),
  );

  // selectedType='none'일 때 MenuItemContent에는 undefined 전달
  const menuItemContentSelectedType = selectedType === 'none' ? undefined : selectedType;
  const resolvedDisableTooltipOnEllipsis =
    disableTooltipOnEllipsis || hasVisibleTooltipMessage(tooltip);

  return (
    <MenuOptionItemRoot
      as={RootSlotComponent}
      ref={ref}
      className={clsx(className, getDynamicClasses(ownerState))}
      color={color}
      ownerState={ownerState}
      selected={selected}
      disabled={disabled}
      disableRipple
      disableTouchRipple
      onClick={disabled || !interactive ? undefined : onClick}
      tabIndex={interactive ? 0 : -1}
      {...rootSlotProps}
      {...others}
    >
      {renderContent ? (
        renderContent({
          color,
          disabled,
          disableTooltipOnEllipsis: resolvedDisableTooltipOnEllipsis,
          ellipsisTooltipPlacement,
          endBadge,
          icon,
          selected,
          selectedType: menuItemContentSelectedType,
          startBadge,
          subTexts: renderedSubTexts,
          suffix,
          text: renderedText,
          tooltip,
          truncateSubTextIndex,
        })
      ) : (
        <Tooltip message={tooltip}>
          <MenuItemContent
            text={renderedText}
            subTexts={renderedSubTexts}
            truncateSubTextIndex={truncateSubTextIndex}
            disableTooltipOnEllipsis={resolvedDisableTooltipOnEllipsis}
            ellipsisTooltipPlacement={ellipsisTooltipPlacement}
            icon={icon}
            startBadge={startBadge}
            endBadge={endBadge}
            selected={selected}
            disabled={disabled}
            selectedType={menuItemContentSelectedType}
            color={color}
            suffix={suffix}
            // 드래그 관련 props
            dragHandleRef={dragHandleRef}
            dragHandleDisabled={dragHandleDisabled}
            showDragHandle={showDragHandle}
            isDragging={isDragging}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        </Tooltip>
      )}
    </MenuOptionItemRoot>
  );
}

MenuOptionItem.displayName = 'MenuOptionItem';

// Styled Components
const MenuOptionItemRoot = styled(MenuItem, {
  target: menuOptionItemClasses.root,
})<{ ownerState: MenuOptionItemOwnerState }>(
  ({ theme, ownerState }) => css`
    min-height: 36px;
    display: block;

    &[aria-disabled='true'] {
      &,
      &.Mui-selected,
      &.Mui-selected:hover {
        pointer-events: auto;
      }
    }

    /* 단일 컴포넌트 사용 시 실제 포커스 스타일 */
    ${ownerState.interactive !== false &&
    theme.mixins.focusRing('inside', {
      inverse: ownerState.color === 'global',
    })};

    /* 가상 포커스 스타일 (DropdownMenu2 등에서 사용) */
    ${ownerState.focused &&
    css`
      ${theme.mixins.focusRing('inside', {
        inverse: ownerState.color === 'global',
        selectors: {
          focus: '&',
        },
      })};
    `}

    ${!ownerState.interactive &&
    css`
      cursor: default;
      &:hover {
        background-color: transparent;
      }
    `}

    ${ownerState.color === 'global' &&
    css`
      && {
        color: ${theme.color['text/global/default']};
      }
    `}
  `,
);
